import { useState } from "react";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { INPUT_LIGHT } from "@/lib/constants";
import { FadeUp, MaskLine, SectionTag } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/enquiries", { name: form.name, email: form.email, company: form.company || null, message: form.message });
      setSent(true);
      toast.success("Enquiry sent. We reply within one business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-40 pb-32">
      <Seo
        title="Contact Samanvay HR — Start a Search"
        description="Tell us the role and we'll find the person. Reach Samanvay HR in Nanakramguda, Hyderabad for hiring, staffing, payroll and HR consulting enquiries."
      />
      <div className="max-w-7xl mx-auto px-6">
        <SectionTag>Contact</SectionTag>
        <h1 className="font-display font-semibold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-slate-900 max-w-4xl">
          <MaskLine delay={0.15}>Tell us the role.</MaskLine>
          <MaskLine delay={0.3}>
            <span className="font-editorial italic font-medium text-brand">We&rsquo;ll find the person.</span>
          </MaskLine>
        </h1>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeUp className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 shadow-sm">
              {sent ? (
                <div className="text-center py-14" data-testid="contact-success">
                  <CheckCircle2 size={44} className="mx-auto text-brand" />
                  <h3 className="mt-6 font-display text-2xl font-semibold text-slate-900">Message received.</h3>
                  <p className="mt-3 text-sm text-slate-500">A specialist recruiter will reply within one business day.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name" className="text-slate-500">Name</Label>
                      <Input id="contact-name" required value={form.name} onChange={set("name")} data-testid="contact-name-input"
                        className={INPUT_LIGHT} placeholder="Your name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email" className="text-slate-500">Work email</Label>
                      <Input id="contact-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input"
                        className={INPUT_LIGHT} placeholder="you@company.com" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-company" className="text-slate-500">Company</Label>
                    <Input id="contact-company" value={form.company} onChange={set("company")} data-testid="contact-company-input"
                      className={INPUT_LIGHT} placeholder="Company Pvt. Ltd." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-message" className="text-slate-500">What are you hiring for?</Label>
                    <Textarea id="contact-message" required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input"
                      className={INPUT_LIGHT}
                      placeholder="Role, team size, timeline — the more specific, the faster we move." />
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="contact-submit-btn"
                    className="rounded-full bg-brand px-8 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow disabled:opacity-50"
                  >
                    {saving ? "Sending..." : "Send enquiry"}
                  </button>
                </form>
              )}
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="space-y-5">
              {[
                { icon: Mail, label: "Email", value: "info@samanvayhr.com", testid: "contact-info-email" },
                { icon: Phone, label: "Phone", value: "+91 91122 33574", testid: "contact-info-phone" },
                { icon: MapPin, label: "Studio", value: "Arenesha, 6th Floor, The District, Near Medicover Hospital, Financial District, Nanakramguda, Hyderabad 500032", testid: "contact-info-address" },
              ].map((c) => (
                <div key={c.label} className="rounded-2xl border border-slate-200 bg-white p-7 flex items-start gap-5 shadow-sm" data-testid={c.testid}>
                  <c.icon size={20} className="text-brand mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-slate-400">{c.label}</p>
                    <p className="mt-2 text-slate-700">{c.value}</p>
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border border-brand/30 bg-orange-50/60 p-7" data-testid="contact-response-note">
                <p className="font-editorial italic text-xl text-slate-600 leading-relaxed">
                  &ldquo;Every enquiry is read by a founder, not a form handler.&rdquo;
                </p>
                <p className="mt-3 text-xs uppercase tracking-[0.25em] text-slate-400">Response within one business day</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
